import React from "react"
import Select, { components, createFilter } from "react-select"
import PropTypes from "prop-types"
import styled from "styled-components"

import THEME from "~/style/theme"
import STYLE_CONSTANTS from "~/style/constants"
import useInject from "~/hooks/useInject"

const mapStore = (store) => ({
  fetcherName: store.trafficLookup.fetcherName,
  isMobile: store.ui.isMobile,
})

const Container = styled.div`
  position: relative;
  width: 100%;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }

  ${({ fullWidth }) => !fullWidth && `
    max-width: 364px;
  `}
`

const ImageContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 50%;
  left: 0;
  width: 50px;
  height: 50px;
  transform: translateY(-50%);
  z-index: 4;

  @media (max-width: 768px) {
    transform: translateY(-60%);
  }

  @media (max-width: 480px) {
    transform: translateY(-60%);
  }
`

const getOptionBackground = (state) => {
  if (state.isSelected) {
    return THEME.colors.primary
  }
  if (state.isFocused) {
    return STYLE_CONSTANTS.colors.ui.activeHightlight
  }
  return "transparent"
}

const getOptionTextColor = (state) => {
  if (state.isSelected) {
    return "#FFFFFF"
  }
  return THEME.colors.text.input
}

const controlBorderColor = (noBorderOnFocus, noBorder, error, state) => {
  let controlBorder = THEME.colors.ui.inputBorder
  if ((noBorderOnFocus && state.isFocused) || noBorder) {
    controlBorder = "rgba(0,0,0,0)"
  }
  // This has priority
  if (error) {
    controlBorder = THEME.colors.error
  }
  return controlBorder
}

const buildStyles = ({
  includesImage,
  error,
  includesLabel,
  noBorderOnFocus,
  noBorder,
  noLabel,
  bold,
}) => {
  return {
    // Value Placeholder
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(25,25,27,0.4)",
      fontSize: "15px",
      marginTop: includesLabel ? "0.5rem" : provided.marginTop,
      fontFamily: THEME.fonts.avenirNextDemi,
      whiteSpace: "noWrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      width: "95%",
    }),
    // Container for menu list
    menu: (provided) => ({
      ...provided,
      border: "none",
      borderRadius: "0 0 4px 4px",
      marginTop: 0,
      zIndex: 5,
      boxShadow: "0px 8px 15px 0px rgba(35, 41, 48, 0.15)",
    }),
    // Holds the list options list
    menuList: (provided) => ({
      ...provided,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: getOptionBackground(state),
      "&:hover": {
        backgroundColor: STYLE_CONSTANTS.colors.ui.activeHightlight,
        color: STYLE_CONSTANTS.colors.text.label,
      },
      cursor: "pointer",
      border: "none",
      color: getOptionTextColor(state),
      padding: includesImage ? "12px 12px 12px 50px" : "9px 16px;",
      fontFamily: bold
        ? THEME.fonts.avenirNextDemi
        : THEME.fonts.avenirNextDemi,
    }),
    input: (provided) => ({
      // ...provided,
      marginTop: includesLabel ? "0.7em" : provided.marginTop,
      fontSize: includesLabel ? "1.2rem" : "1rem",
      color: includesLabel ? "#000" : STYLE_CONSTANTS.colors.text.label,
      fontFamily: bold
        ? THEME.fonts.avenirNextDemi
        : THEME.fonts.avenirNextDemi,
      outline: "none",
      border: "0",
      background: noLabel ? "white" : "",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: () => ({
      display: "block",
      marginRight: "10px",
      color: "#19191b",
      opacity: 0.6,
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: includesLabel ? "1.2rem" : "1rem",
      color: THEME.colors.text.label,
      fontFamily: bold
        ? THEME.fonts.avenirNextDemi
        : THEME.fonts.avenirNextDemi,
      marginTop: includesLabel ? "0.5rem" : provided.marginTop,
      marginLeft: includesLabel ? 0 : provided.marginLeft,
    }),
    // Outer most div. Used to container input and options
    container: (provided) => ({
      ...provided,
      borderRadius: 4,
      height: includesLabel || noLabel ? 70 : 70,
      padding: "0",
      boxShadow: null,
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      fontSize: "1rem",
      fontFamily: bold
        ? THEME.fonts.avenirNextDemi
        : THEME.fonts.avenirNextDemi,
    }),
    // Holds/Displays the actual input value
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
      marginLeft: includesImage ? "50px" : "14px",
      fontSize: includesLabel ? "1.2rem" : "1rem",
      fontFamily: bold
        ? THEME.fonts.avenirNextDemi
        : THEME.fonts.avenirNextDemi,
      marginTop: includesLabel ? "0.5rem" : provided.marginTop,
    }),
    // Wraps around Value Container
    control: (provided, state) => ({
      position: "relative",
      outline: "0",
      transition: "all 100ms",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-between",
      alignItems: "center",
      borderColor: noLabel
        ? STYLE_CONSTANTS.colors.ui.borderGray
        : controlBorderColor(noBorderOnFocus, noBorder, error, state),
      backgroundColor: "#ffffff",
      borderRadius: 4,
      borderStyle: "solid",
      borderWidth: 1,
      zIndex: 3,
      minHeight: 38,
      height: includesLabel || noLabel ? 70 : 70,
      background: "#FFFFFF",
      boxShadow: "0px 8px 15px 0px rgba(35, 41, 48, 0.15)",
      "@media (max-width: 768px)": {
        height: includesLabel || noLabel ? 56 : 56,
      },
      "@media (max-width: 480px)": {
        height: includesLabel || noLabel ? 48 : 48,
      },
    }),
  }
}

const Text = styled.p`
  position: absolute;
  left: 16px;
  top: 0px;
  font-size: 0.9em;
  color: ${(props) => props.theme.colors.text.label};
  z-index: 5;
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
`

const ControlContainer = styled.div`
  ${(props) => props.theme.colors.text.label}
`

const Dropdown = ({
  noLabel,
  autoComplete,
  imgSrc,
  onChange,
  options,
  value,
  id,
  error,
  controlLabel,
  noBorderOnFocus,
  noBorder,
  bold,
  searchStrategy,
  fullWidth = false,
  styles: customStyles,
  ...props
}) => {
  const Input = ({
    innerRef,
    getStyles,
    isDisabled,
    isHidden,
    cx,
    selectProps,
    theme,
    ...inputProps
  }) => {
    return (
      <input
        ref={innerRef}
        style={getStyles("input", { ...inputProps, theme })}
        disabled={isDisabled}
        {...inputProps}
        id={id}
        autoComplete={autoComplete}
      />
    )
  }

  const Control = ({ ...controlProps }) => {
    if (process.browser) {
      return controlLabel ? (
        <ControlContainer>
          <Text>{controlLabel}</Text>
          <components.Control {...controlProps} />
        </ControlContainer>
      ) : (
        <components.Control {...controlProps} />
      )
    }
    return null
  }

  const { fetcherName } = useInject(mapStore)

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: searchStrategy,
  }

  const builtStyles = buildStyles({
    includesImage: !!imgSrc,
    error: !!error,
    includesLabel: !!controlLabel,
    noBorderOnFocus,
    noBorder,
    noLabel,
    bold,
  })
  const styles = customStyles
    ? Object.keys(builtStyles).reduce(
        (acc, key) => {
          const customFn = customStyles[key]
          acc[key] = customFn
            ? (provided, state) => ({
                ...builtStyles[key](provided, state),
                ...(typeof customFn === 'function' ? customFn(provided, state) : customFn),
              })
            : builtStyles[key]
          return acc
        },
        {}
      )
    : builtStyles

  return (
    <Container fetcherName={fetcherName} fullWidth={fullWidth}>
      {imgSrc ? (
        <ImageContainer>
          <img src={imgSrc} alt="" />
        </ImageContainer>
      ) : null}
      <Select
        {...props}
        // classNamePrefix="react-select"
        styles={styles}
        value={value}
        onChange={onChange}
        options={options}
        components={{
          Input,
          Control,
          ...(props.components || {}),
        }}
        filterOption={createFilter(filterConfig)}
      />
    </Container>
  )
}

Dropdown.defaultProps = {
  autoComplete: "off",
  imgSrc: null,
  value: null,
  error: false,
  noBorderOnFocus: false,
  noBorder: false,
  controlLabel: "",
  bold: true,
  searchStrategy: "any",
}

Dropdown.propTypes = {
  autoComplete: PropTypes.string,
  imgSrc: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
    PropTypes.object,
  ]),
  controlLabel: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  noBorderOnFocus: PropTypes.bool,
  noBorder: PropTypes.bool,
  bold: PropTypes.bool,
  searchStrategy: PropTypes.string,
}

export default Dropdown
